/// <amd-module name="Artifacts/Connect/components/ConnectToFeed" />
import * as Res from "Artifacts/Connect/Resources";
import { WidthBreakpoint } from "Artifacts/Connect/components/Common";
import { FirstTimeMessageCard } from "Artifacts/Connect/components/FirstTimeMessage";
import { GetToolPanel } from "Artifacts/Connect/components/GetToolPanel";
import { ToolIcon } from "Artifacts/Connect/components/ToolIcon";
import { PackagingService, TfsService } from "Artifacts/Connect/services/PackagingService";
import { ICombinedFeedView } from "Artifacts/Connect/types/ICombinedFeedView";
import { ITool, ToolId } from "Artifacts/Connect/types/ITool";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { Uri } from "VSS/Platform/Util/Url";
import { Button } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { SingleLayerMasterPanel, SingleLayerMasterPanelHeader } from "VSSUI/MasterDetails";
import {
  ITabGroup,
  ITabProps,
  IVssContributedTab,
  TabList,
  TabProvider,
  TabSize,
} from "VSSUI/Tabs";
import { css } from "VSSUI/Util";
import { IItemProvider } from "VSSUI/Utilities/Provider";
import * as React from "react";

export type IToolTabCell = ITabProps & { tool?: ITool };

export interface IConnectToFeedProps {
  onBackButtonClick: () => void;
  onSelectedToolBackClick: () => void;
  pageContext: IVssPageContext;
  itemProvider: IItemProvider<IToolTabCell>;
  protocolNames: string[];
  selectedTool?: ITool;
  combinedFeedView?: ICombinedFeedView;
  dismissedTools: ToolId[];
  packagingUrl: string;
  sessionTokenUri: Uri;
  onDismissFirstTimeMessage: (toolId: ToolId) => void;
  selectTool: (toolId?: string) => void;
}

const ConnectToFeed: React.FC<IConnectToFeedProps> = ({
  itemProvider,
  protocolNames,
  selectedTool,
  pageContext,
  onBackButtonClick,
  onSelectedToolBackClick,
  combinedFeedView,
  dismissedTools,
  packagingUrl,
  sessionTokenUri,
  onDismissFirstTimeMessage,
  selectTool,
}) => {
  const packagingService = new PackagingService(packagingUrl);
  const tfsService = new TfsService(sessionTokenUri);

  const [showGetTools, setshowGetTools] = React.useState<boolean>(false);

  function getOverview(tool: ITool, onSelectedToolBackClick: () => void) {
    return (
      <div className="overview">
        <div className="overview-heading flex-center">
          <WidthBreakpoint widthLessThan={ScreenBreakpoints.medium}>
            {onSelectedToolBackClick && (
              <Button
                ariaLabel={Res.Back}
                className={"bolt-header-back-button justify-end"}
                iconProps={{
                  iconName: "Back",
                  size: undefined,
                }}
                subtle={true}
                onClick={onSelectedToolBackClick}
              />
            )}
          </WidthBreakpoint>

          <ToolIcon tool={tool} className="icon-large-margin" />
          <h2 className="title-m flex-grow">{tool.name}</h2>
          <Button text={Res.GetTools_Text} primary={true} onClick={() => setshowGetTools(true)} />
        </div>

        {tool.getToolLink && (
          <div className="flex-row">
            <span className="icon-large-margin" />
            <Link rel="nofollow noreferrer noopener help" target="_blank" {...tool.getToolLink()} />
            <Icon iconName="NavigateExternalInline" />
          </div>
        )}
      </div>
    );
  }

  function shouldRenderFirstTimeMessageCard(tool: ITool) {
    // The toolId should not be in the dismissed tools.
    return dismissedTools.indexOf(tool.id) === -1;
  }

  function processItemProvider() {
    const groups: ITabGroup[] = [];
    const providers = new ObservableArray<IVssContributedTab>();

    protocolNames.forEach((protocol, index) => {
      groups.push({
        id: protocol,
        name: protocol,
        order: index + 1,
      });
    });
    itemProvider.value.forEach((item) => {
      if (item.tool) {
        const toolIcon = (
          <span aria-hidden="true" className="tool-icon-wrapper">
            <ToolIcon tool={item.tool} className="icon-large-margin" />
          </span>
        );
        providers.push({
          id: item.tool.name,
          groupId: item.tool.protocol.name,
          name: item.name,
          iconProps: {
            className: "icon-large-margin",
            render: () => toolIcon,
          },
        });
      }
    });

    const defaultTabId = new ObservableValue(selectedTool ? selectedTool.name : "");

    return (
      <section aria-label={Res.PackageTypes} className="tool-selector depth-4">
        <TabProvider providers={providers} selectedTabId={defaultTabId}>
          <TabList
            className="tab-item"
            key={1}
            onSelectedTabChanged={selectTool}
            selectedTabId={defaultTabId}
            tabSize={TabSize.LargeLink}
            tabGroups={groups}
          ></TabList>
          {/* <TabContent /> */}
        </TabProvider>
      </section>
    );
  }

  return combinedFeedView ? (
    <div className={css("connect-to-feed-view flex-row body-m", selectedTool && "tool-selected")}>
      <SingleLayerMasterPanel
        className="master-panel"
        renderHeader={() => (
          <SingleLayerMasterPanelHeader
            title={Res.ConnectToFeed}
            subTitle={combinedFeedView.name}
            onBackButtonClick={onBackButtonClick}
          />
        )}
        renderContent={() => processItemProvider()}
        showOnSmallScreens={!selectedTool}
      />
      {selectedTool && (
        <section
          className="tool-details flex-grow scroll-auto"
          aria-label={Utils_String.format(
            Res.AriaLabel_ToolDetails,
            selectedTool.protocol.name,
            selectedTool.name,
          )}
        >
          {getOverview(selectedTool, () => {
            onSelectedToolBackClick();
          })}
          <div className="rhythm-vertical-16">
            {shouldRenderFirstTimeMessageCard(selectedTool) && (
              <FirstTimeMessageCard
                tool={selectedTool}
                className="firstTimeMessage flex-self-start"
                onGetTools={() => setshowGetTools(true)}
                onDismiss={() => onDismissFirstTimeMessage(selectedTool.id)}
              />
            )}
            {selectedTool.renderComponent(
              pageContext,
              combinedFeedView,
              packagingService,
              tfsService,
            )}
          </div>
        </section>
      )}
      {showGetTools && selectedTool && (
        <GetToolPanel
          pageContext={pageContext}
          tool={selectedTool}
          onDismiss={() => setshowGetTools(false)}
        />
      )}
    </div>
  ) : null;
};

export { ConnectToFeed };
